import {
  PathPickOrder,
  PathPickOrderDetailResponse,
} from "@/features/pathpick/features/order-history/types/response";
import { PathPickProduct } from "@/features/pathpick/types";
import { START_GUIDE_IMAGES } from "@/features/pathpick/features/start-guide/constants";
import {
  DEMO_HOT_DEAL_PRODUCT,
  DEMO_NORMAL_PRODUCT,
} from "@/features/pathpick/features/start-guide/utils/demoProducts";

// 고정 시각을 쓴다 — 모듈 로드 시각에 따라 스텝퍼나 마감 안내가 흔들리면 안 된다.
const ORDERED_AT = "2026-08-20T02:00:00Z";
const REMITTANCE_DUE_AT = "2026-08-22T05:00:00Z";
const QUANTITY_CONFIRMED_AT = "2026-08-21T02:00:00Z";

// 주문 수량은 목 상품의 사이즈 재고를 그대로 쓴다 — `상품 담기`의 수량 모달이 허용하는 범위와
// 어긋나면 같은 두 상품을 주문했다는 전제가 깨진다.
const toOrderedQuantity = (product: PathPickProduct) =>
  product.options[0]?.stock ?? 1;

function toOrderItem(
  product: PathPickProduct,
  id: number,
  imageUrl: string
): PathPickOrder["items"][number] {
  const quantity = toOrderedQuantity(product);
  const amount = product.salePrice.value * quantity;
  return {
    id,
    productType: product.productType,
    productId: product.id,
    lineSheetSetId: null,
    lineSheetId: null,
    lineSheetTabIndex: null,
    brandId: id,
    brandName: product.brandName,
    sku: product.sku,
    productName: product.name,
    imageUrls: imageUrl ? [imageUrl] : [],
    color: product.color,
    isHotdeal: product.productType === "LINE_SHEET_PRODUCT",
    isQuantityConfirmed: true,
    salePrice: product.salePrice.value,
    currency: "KRW",
    orderedQuantity: quantity,
    orderedAmount: amount,
    confirmedQuantity: quantity,
    canceledQuantity: 0,
    confirmedAmount: amount,
    options: product.options.slice(0, 1).map((option, optionIndex) => ({
      id: id * 10 + optionIndex,
      name: option.name,
      orderedQuantity: quantity,
      confirmedQuantity: quantity,
      canceledQuantity: 0,
      confirmedAmount: amount,
      shippedQuantity: 0,
    })),
  };
}

const ITEMS = [
  toOrderItem(DEMO_NORMAL_PRODUCT, 1, START_GUIDE_IMAGES.celine),
  toOrderItem(DEMO_HOT_DEAL_PRODUCT, 2, START_GUIDE_IMAGES.gucci),
];

const TOTAL_QUANTITY = ITEMS.reduce(
  (sum, item) => sum + item.orderedQuantity,
  0
);
const TOTAL_AMOUNT = ITEMS.reduce((sum, item) => sum + item.orderedAmount, 0);

/**
 * 주문 상세 안내에서 쓰는 목 주문 — 판매 방식과 무관하게 같은 샘플을 쓴다.
 *
 * `상품 담기` 탭의 두 목 상품을 모두 주문해 수량까지 확정된 상태다.
 * `quantityConfirmedAt` 이 채워져 있어야 KREAM 파일 요청 카드의 신청 버튼이 열린다 —
 * 확정 여부는 합계가 아니라 이 시각으로 판별하기 때문이다.
 *
 * `shipments` 와 `invoice` 는 비워 둔다. 수량 확정 직후는 아직 미발송이고 거래명세서도
 * 등록 전이라, 배송/명세서 카드가 안내 문구와 비활성 버튼으로 떨어지는 것이 실제 화면이다.
 */
export const DEMO_ORDER: PathPickOrderDetailResponse = {
  id: 900001,
  orderNumber: "PP-GUIDE-0001",
  buyerId: 0,
  status: "QUANTITY_CONFIRMED",
  orderedAt: ORDERED_AT,
  quantityConfirmedAt: QUANTITY_CONFIRMED_AT,
  totalOrderedQuantity: TOTAL_QUANTITY,
  totalOrderedAmount: TOTAL_AMOUNT,
  totalConfirmedQuantity: TOTAL_QUANTITY,
  totalConfirmedAmount: TOTAL_AMOUNT,
  totalCanceledQuantity: 0,
  currency: "KRW",
  remittance: {
    amount: TOTAL_AMOUNT,
    dueAt: REMITTANCE_DUE_AT,
    completedAt: QUANTITY_CONFIRMED_AT,
    bankName: "우리은행",
    accountNumber: "1005-804-502502",
    accountHolder: "주식회사 유노트레이딩",
  },
  shippingInfo: {
    alias: "기본 배송지",
    recipientName: "김패스",
    contact: "010-1234-5678",
    address: "서울특별시 강남구 테헤란로 123",
    detailAddress: "패스레이드빌딩 4층",
    zipCode: "06133",
  },
  shipments: [],
  brandNames: ITEMS.map((item) => item.brandName),
  itemCount: ITEMS.length,
  hasHotdeal: ITEMS.some((item) => item.isHotdeal),
  representativeSku: ITEMS[0].sku,
  items: ITEMS,
  // 아직 아무것도 신청하지 않은 상태 — 가이드가 KREAM 파일 신청 버튼을 눌러야 진행된다.
  requestIds: [],
};

/**
 * 주문 상세 미리보기의 환불 계좌 칸을 채우는 목 계좌.
 *
 * 실제 카드는 바이어 계좌를 조회해 인풋에 채우는데, 가이드에서는 그 조회를 걸지 않으므로
 * 빈 인풋 대신 이 값을 비활성 상태로 보여준다.
 */
export const DEMO_REFUND_ACCOUNT = {
  bankName: "국민은행",
  accountNumber: "123456-01-789012",
  accountHolder: "김패스",
} as const;
